import React from 'react'
import { Modal, Table, Badge } from 'react-bootstrap'

const API = () => <Badge bg="info" className="me-1" style={{ fontSize: '0.7rem' }}>API</Badge>
const CALC = () => <Badge bg="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>CALC</Badge>

function DailyDashboardGuideModal({ show, onHide }) {
    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Daily Dashboard &mdash; Column Reference</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">
                    <API /> Value from Amazon API (may be aggregated)
                    &nbsp;&nbsp;
                    <CALC /> Calculated by the system
                </p>
                <p className="text-muted mb-3">
                    The dashboard date is the <strong>last sales day included</strong>: every rolling window
                    (sales, traffic, ads) ends on that day. Amazon data for a day is collected the following
                    evening, so the default date is the newest day that already has economics data, usually
                    two days ago. Sales and traffic sums use single-day DataKiosk records only; multi-day
                    on-demand downloads that overlap them are ignored here.
                </p>
                <p className="text-muted mb-3">
                    <strong>Fulfillment channel.</strong> The Amazon / Seller / Both selector (default Amazon) picks which
                    orders count toward Units Sold, Revenue and the day buckets. Amazon = orders fulfilled by Amazon (FBA),
                    Seller = fulfilled by the merchant (FBM). These figures come from the All Orders report by order date in
                    Pacific time, Amazon.com sales channel only, the same basis as the Seller Central Sales Dashboard.
                    Fees, COGS, ads and traffic are per ASIN and do not change with the channel. &ldquo;Both&rdquo; uses the
                    DataKiosk economics figures. If orders data has not been collected for the window, both channels are shown
                    and a warning appears.
                </p>

                <h6 className="mb-2">Configurable Parameters</h6>
                <Table size="sm" bordered className="mb-4">
                    <thead>
                        <tr>
                            <th>Setting</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>Default</th>
                            <th>Meaning</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>units_sold_rolling_days</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>30</td>
                            <td>Length of the rolling window, ending on the dashboard date, for units sold, revenue, and fees (daily records only)</td>
                        </tr>
                        <tr>
                            <td><code>inventory_low_stock_months</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>2</td>
                            <td>If month-supply &lt; 2 months, mark as &ldquo;Low stock&rdquo;</td>
                        </tr>
                        <tr>
                            <td><code>sales_level_best_threshold</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>600</td>
                            <td>If monthly units &ge; 600, label &ldquo;Best Seller&rdquo;</td>
                        </tr>
                        <tr>
                            <td><code>sales_level_medium_threshold</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>200</td>
                            <td>If monthly units &ge; 200 (but &lt; 600), label &ldquo;Medium Seller&rdquo;; below 200 = &ldquo;Slow Seller&rdquo;</td>
                        </tr>
                        <tr>
                            <td><code>ad_lookback_days</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>30</td>
                            <td>Length of the rolling window, ending on the dashboard date, for ad and traffic metrics (impressions, clicks, sessions, ad spend, ACoS)</td>
                        </tr>
                        <tr>
                            <td><code>referral_fee_rate</code></td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>0.08</td>
                            <td>Referral fee rate (e.g., 0.08 = 8% for Grocery). Overrides the DataKiosk weighted average with <code>sale_price &times; rate</code></td>
                        </tr>
                    </tbody>
                </Table>

                <h6 className="mb-2">Column Definitions</h6>
                <Table size="sm" bordered hover>
                    <thead>
                        <tr>
                            <th>Column</th>
                            <th style={{ width: '90px', textAlign: 'center' }}>Source</th>
                            <th>Calculation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="table-active"><td colSpan="3"><strong>Identity</strong></td></tr>
                        <tr>
                            <td>ASIN</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Union of all ASINs found across ad, inventory, economics, traffic, and planning data sources</td>
                        </tr>
                        <tr>
                            <td>Amazon SKU</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From economics data; fallback to <code>inv.sku</code> from FBA inventory report</td>
                        </tr>
                        <tr>
                            <td>Product Name</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From inventory report; fallback to SKU Mapping product name</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Pricing &amp; Fees (per unit)</strong></td></tr>
                        <tr>
                            <td>Sale Price</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>FBA inventory <code>your_price</code>; fallback: <code>revenue / units_sold</code> when both &gt; 0</td>
                        </tr>
                        <tr>
                            <td>FBA Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit gross <code>FbaFulfilment</code> fee from DataKiosk economics records</td>
                        </tr>
                        <tr>
                            <td>Referral Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit referral fee. When <code>referral_fee_rate</code> is configured, calculated as <code>sale_price &times; rate</code>; otherwise from DataKiosk economics records</td>
                        </tr>
                        <tr>
                            <td>Inbound Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>
                                Sum of <code>FbaInboundConvenience</code> + <code>FbaInboundTransportation</code> charges in the <code>rolling_days</code> window, divided by units sold in the same window (how Seller Central SKU Economics shows it).
                                <br /><small className="text-muted">A sub-fee that never appears in the daily data (typically transportation) is taken from the latest monthly DataKiosk record, divided by that month&apos;s units sold</small>
                            </td>
                        </tr>
                        <tr>
                            <td>Storage Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>FbaStorage</code> + <code>LongTermStorage</code> + <code>STARStorage</code> fee from DataKiosk; falls back to latest monthly DataKiosk record if not in daily data</td>
                        </tr>
                        <tr>
                            <td>COGS</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>From DataKiosk economics; fallback to SKU Mapping stock item cost</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Profitability</strong></td></tr>
                        <tr>
                            <td>Profit</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>sale_price &minus; (cogs + fba_fee + inbound_fee + referral_fee + storage_fee + ad_cost_per_unit)</code></td>
                        </tr>
                        <tr>
                            <td>Margin %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(profit / cogs) &times; 100</code></td>
                        </tr>
                        <tr>
                            <td>Break-Even ACoS</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>((sale_price &minus; cost_before_ad) / sale_price) &times; 100</code><br /><small className="text-muted">cost_before_ad = cogs + all 4 fees</small></td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Advertising (configurable window)</strong></td></tr>
                        <tr>
                            <td>Ad Spend</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of <code>cost</code> from ad performance data over the <code>ad_lookback_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Ad Sales</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of <code>ad_sales</code> from ad performance data over the <code>ad_lookback_days</code> window</td>
                        </tr>
                        <tr>
                            <td>ACoS</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(ad_spend / ad_sales) &times; 100</code>, displayed as percentage</td>
                        </tr>
                        <tr>
                            <td>Ad Conversion</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>total_purchases / total_clicks</code></td>
                        </tr>
                        <tr>
                            <td>Impressions</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of impressions over the <code>ad_lookback_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Clicks</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of clicks over the <code>ad_lookback_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Ad Cost/Unit</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>ad_spend / units_sold</code> over the ad lookback window</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Traffic &amp; Sales</strong></td></tr>
                        <tr>
                            <td>Sessions</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of sessions from daily traffic records over the <code>ad_lookback_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Conversion %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(units_ordered / sessions) &times; 100</code> from traffic data</td>
                        </tr>
                        <tr>
                            <td>Share by Ad %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(ad_sales / revenue) &times; 100</code></td>
                        </tr>
                        <tr>
                            <td>Units Sold</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of <code>units_sold</code> from daily economics records over the <code>rolling_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Revenue</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Sum of <code>revenue</code> from daily economics records over the <code>rolling_days</code> window</td>
                        </tr>
                        <tr>
                            <td>Units 1-10d</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>Units sold during the oldest 10 days of the 30-day window (days 1&ndash;10 counted from the start of the window)</td>
                        </tr>
                        <tr>
                            <td>Units 11-20d</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>Units sold during days 11&ndash;20 of the 30-day window</td>
                        </tr>
                        <tr>
                            <td>Units 21-30d</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>Units sold during the most recent 10 days, ending on the dashboard date (days 21&ndash;30 of the 30-day window)</td>
                        </tr>
                        <tr>
                            <td>Sales Level</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>monthly_units = (units_sold / rolling_days) &times; 30</code>
                                <br /><small className="text-muted">&ge; <code>sales_level_best_threshold</code> &rarr; Best Seller; &ge; <code>sales_level_medium_threshold</code> &rarr; Medium Seller; else Slow Seller</small>
                            </td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Inventory</strong></td></tr>
                        <tr>
                            <td>Inventory Qty</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td><code>Inventory Supply at FBA</code> from Inventory Planning report; fallback to <code>afn_total_quantity</code> from FBA inventory report</td>
                        </tr>
                        <tr>
                            <td>Available Inv.</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td><code>available</code> from Inventory Planning report; fallback to <code>afn_fulfillable_quantity</code> from FBA inventory report</td>
                        </tr>
                        <tr>
                            <td>Inventory Level</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>month_supply = inventory_qty / ((units_sold / rolling_days) &times; 30)</code>
                                <br /><small className="text-muted">0 &rarr; Out of stock; &lt; <code>inventory_low_stock_months</code> &rarr; Low stock; else Enough</small>
                            </td>
                        </tr>
                        <tr>
                            <td>Health Status</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From FBA Inventory Planning report</td>
                        </tr>
                        <tr>
                            <td>Low Inventory Fee</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Boolean from FBA Inventory Planning report</td>
                        </tr>
                    </tbody>
                </Table>
            </Modal.Body>
        </Modal>
    )
}

export default DailyDashboardGuideModal
